import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLearnerAuth } from '../context/LearnerAuthContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  BookOpen, 
  Calendar, 
  Clock, 
  Target, 
  TrendingUp, 
  Award,
  ChevronRight,
  Mic,
  Video,
  LogOut,
  User,
  CheckCircle,
  AlertCircle,
  Play,
  RotateCcw,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const HFS_LOGO = "https://customer-assets.emergentagent.com/job_skill-profiles/artifacts/qxumdsjc_HFS_logo_192x192.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api/learn`;

export default function LearnerDashboard() {
  const navigate = useNavigate();
  const { learner, token, isAuthenticated, loading: authLoading, logout, refreshLearner } = useLearnerAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/learn-communication');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    const fetchDashboard = async () => {
      if (!token) return;
      
      try {
        const response = await fetch(`${API}/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setDashboard(data);
        } else {
          toast.error('Failed to load dashboard');
        }
      } catch (error) {
        console.error('Dashboard error:', error);
        toast.error('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [token]);

  const handleLogout = () => {
    logout();
    navigate('/learn-communication');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'in_progress':
        return <Play className="h-5 w-5 text-blue-500" />;
      case 'rework_required':
        return <RotateCcw className="h-5 w-5 text-orange-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-slate-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'in_progress':
        return 'In Progress';
      case 'rework_required':
        return 'Rework Required';
      default:
        return 'Not Started';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'rework_required':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-slate-100 text-slate-600';
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-hfs-navy mx-auto mb-4" />
          <p className="text-slate-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/learn-communication" className="flex items-center gap-3">
              <img src={HFS_LOGO} alt="HFS" className="h-8 w-8" />
              <span className="font-bold text-hfs-navy">Learn Communication</span>
            </Link>
            
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-2 text-sm text-slate-600">
                <User className="h-4 w-4" />
                <span>{dashboard.learner_name}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <main className="pt-20 pb-12 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Welcome Section */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-hfs-navy mb-2">
              Welcome back, {dashboard.learner_name?.split(' ')[0]}! 👋
            </h1>
            <p className="text-slate-600">
              {dashboard.days_completed === 0 
                ? "Your 30-day communication journey starts today!" 
                : `You've completed ${dashboard.days_completed} days. Keep going!`}
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3 mb-2">
                <Calendar className="h-5 w-5 text-blue-500" />
                <span className="text-sm text-slate-500">Current Day</span>
              </div>
              <p className="text-2xl font-bold text-hfs-navy">Day {dashboard.current_day}</p>
              <p className="text-xs text-slate-400">{30 - dashboard.current_day + 1} days remaining</p>
            </div>
            
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="h-5 w-5 text-green-500" />
                <span className="text-sm text-slate-500">Progress</span>
              </div>
              <p className="text-2xl font-bold text-hfs-navy">{dashboard.overall_progress}%</p>
              <Progress value={dashboard.overall_progress} className="h-2 mt-2" />
            </div>
            
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle className="h-5 w-5 text-purple-500" />
                <span className="text-sm text-slate-500">Completed</span>
              </div>
              <p className="text-2xl font-bold text-hfs-navy">{dashboard.days_completed}/30</p>
              <p className="text-xs text-slate-400">days finished</p>
            </div>
            
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="h-5 w-5 text-orange-500" />
                <span className="text-sm text-slate-500">Practice Time</span>
              </div>
              <p className="text-2xl font-bold text-hfs-navy">
                {Math.round(dashboard.total_practice_time / 60)}m
              </p>
              <p className="text-xs text-slate-400">total time spent</p>
            </div>
          </div>

          {/* Today's Task Card */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-hfs-navy to-blue-800 p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 p-2 rounded-lg">
                    <Target className="h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">Today's Task</h2>
                    <p className="text-white/80 text-sm">Day {dashboard.today.day_number} • {dashboard.today.phase}</p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(dashboard.today.status)}`}>
                  {getStatusText(dashboard.today.status)}
                </span>
              </div>
              
              <h3 className="text-lg font-semibold mb-2">{dashboard.today.title}</h3>
              <p className="text-white/80 mb-4">Skill Focus: {dashboard.today.skill_focus}</p>
              
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>{dashboard.today.time_required} mins</span>
                </div>
                <div className="flex items-center gap-2">
                  {dashboard.today.requires_video ? (
                    <>
                      <Video className="h-4 w-4" />
                      <span>Video Required</span>
                    </>
                  ) : (
                    <>
                      <Mic className="h-4 w-4" />
                      <span>Audio Task</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <Button 
                onClick={() => navigate(`/learn-communication/day/${dashboard.today.day_number}`)}
                className="w-full md:w-auto bg-hfs-navy hover:bg-hfs-navy-dark h-12 text-base"
                data-testid="start-task-btn"
              >
                {dashboard.today.status === 'not_started' ? 'Start Task' : 
                 dashboard.today.status === 'rework_required' ? 'Retry Task' :
                 dashboard.today.status === 'completed' ? 'Review Task' : 'Continue Task'}
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Journey Progress */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-bold text-hfs-navy mb-4 flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Your 30-Day Journey
            </h2>
            
            <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-10 gap-2">
              {Array.from({ length: 30 }, (_, i) => {
                const day = i + 1;
                const isCompleted = dashboard.days_completed >= day || 
                  (dashboard.recent_scores && dashboard.recent_scores[day]);
                const isCurrent = day === dashboard.current_day;
                const isLocked = day > dashboard.current_day;
                
                return (
                  <button
                    key={day}
                    onClick={() => !isLocked && navigate(`/learn-communication/day/${day}`)}
                    disabled={isLocked}
                    className={`
                      aspect-square rounded-lg text-sm font-medium flex items-center justify-center
                      transition-all duration-200
                      ${isCompleted ? 'bg-green-500 text-white' : ''}
                      ${isCurrent && !isCompleted ? 'bg-hfs-navy text-white ring-2 ring-hfs-gold' : ''}
                      ${isLocked ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : ''}
                      ${!isCompleted && !isCurrent && !isLocked ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : ''}
                    `}
                    data-testid={`day-${day}-btn`}
                  >
                    {isCompleted ? <CheckCircle className="h-4 w-4" /> : day}
                  </button>
                );
              })}
            </div>
            
            <div className="flex items-center gap-6 mt-4 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-green-500" />
                <span>Completed</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-hfs-navy ring-2 ring-hfs-gold" />
                <span>Current</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-slate-100" />
                <span>Upcoming</span>
              </div>
            </div>
          </div>

          {/* Final Assessment Link (if completed) */}
          {dashboard.days_completed >= 30 && (
            <div className="mt-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-6 text-white">
              <div className="flex items-center gap-4">
                <Award className="h-12 w-12" />
                <div>
                  <h2 className="text-xl font-bold">Congratulations! 🎉</h2>
                  <p>You've completed all 30 days. View your final assessment.</p>
                </div>
                <Button 
                  onClick={() => navigate('/learn-communication/assessment')}
                  className="ml-auto bg-white text-green-600 hover:bg-green-50"
                >
                  View Results
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
